import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Mail, Loader2, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';

export default function CustomerFollowUpPanel({ customers = [] }) {
  const [open, setOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [email, setEmail] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const selectedUser = useMemo(
    () => customers.find((u) => u.id === selectedCustomerId) || null,
    [customers, selectedCustomerId]
  );

  const selectedEmail = selectedUser?.email || '';

  const { data: orders = [], isLoading: ordersLoading } = useQuery({
    queryKey: ['customer-orders', selectedEmail],
    queryFn: () => base44.entities.Order.filter({ customer_email: selectedEmail }, '-created_date', 50),
    enabled: !!selectedEmail,
  });

  const selectedOrder = useMemo(
    () => orders.find((o) => o.id === selectedOrderId) || null,
    [orders, selectedOrderId]
  );

  const handleCustomerChange = (e) => {
    const id = e.target.value;
    setSelectedCustomerId(id);
    setSelectedOrderId('');
    const user = customers.find((u) => u.id === id);
    setEmail(user?.email || '');
    setSuccess(false);
    setError('');
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please select a customer.'); return; }
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      await base44.functions.invoke('sendFollowUpEmail', {
        customerName: selectedUser?.full_name || [selectedUser?.first_name, selectedUser?.last_name].filter(Boolean).join(' ') || '',
        customerEmail: email,
        orderProduct: selectedOrder?.product_name || '',
        orderReference: selectedOrder?.square_order_id || selectedOrder?.id || '',
      });
      setSuccess(true);
      setSelectedCustomerId('');
      setEmail('');
      setSelectedOrderId('');
    } catch (err) {
      setError('Failed to send email. Please try again.');
    }
    setLoading(false);
  };

  const selectClass = 'w-full h-10 bg-[#111115] border border-white/10 text-white px-3 text-sm focus:border-yellow-500/40 focus:outline-none';

  return (
    <div className="border border-yellow-500/20 bg-yellow-500/5">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <Mail className="w-4 h-4 text-yellow-400" />
          <div>
            <p className="text-yellow-400 text-xs font-bold tracking-widest">CUSTOMER FOLLOW-UP EMAIL</p>
            <p className="text-white/30 text-xs mt-0.5">Send the order issue explanation email to a customer</p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
      </button>

      {open && (
        <form onSubmit={handleSend} className="px-5 pb-5 border-t border-yellow-500/10 pt-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">CUSTOMER *</p>
              <select
                value={selectedCustomerId}
                onChange={handleCustomerChange}
                className={selectClass}
                required
              >
                <option value="">Select a customer…</option>
                {customers.map((u) => {
                  const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.full_name || '—';
                  return (
                    <option key={u.id} value={u.id}>
                      {name} — {u.email}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">CUSTOMER EMAIL (AUTO-FILLED)</p>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="customer@example.com"
                className="bg-[#111115] border-white/10 text-white h-10 placeholder:text-white/20 focus:border-yellow-500/40"
                required
              />
            </div>
          </div>

          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">RELATED ORDER (OPTIONAL)</p>
            {!selectedEmail ? (
              <select disabled className={`${selectClass} opacity-40 cursor-not-allowed`}>
                <option>Select a customer first…</option>
              </select>
            ) : ordersLoading ? (
              <div className="flex items-center gap-2 text-white/40 text-sm h-10 px-3">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading orders…
              </div>
            ) : orders.length === 0 ? (
              <select disabled className={`${selectClass} opacity-40 cursor-not-allowed`}>
                <option>No orders found for this customer</option>
              </select>
            ) : (
              <select
                value={selectedOrderId}
                onChange={(e) => setSelectedOrderId(e.target.value)}
                className={selectClass}
              >
                <option value="">No specific order</option>
                {orders.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.product_name || 'Unknown product'} — £{Number(o.amount || 0).toFixed(2)} — {o.status || 'pending'} — {o.created_date ? format(new Date(o.created_date), 'dd MMM yyyy') : '—'}
                  </option>
                ))}
              </select>
            )}
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}

          {success && (
            <div className="flex items-center gap-2 text-green-400 text-xs">
              <CheckCircle2 className="w-4 h-4" /> Follow-up email sent successfully.
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-xs tracking-widest px-5 py-2.5 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {loading ? 'SENDING...' : 'SEND FOLLOW-UP EMAIL'}
          </button>
        </form>
      )}
    </div>
  );
}